// Canonical "what's on day D" rules — the day-view read projection.
//
// Given a trip's bookings, one day's itinerary slots and an ISO date,
// this module decides which rows the day shows, at what time, in what
// order, and what sits behind the day as backdrop (tonight's hotel, the
// hotel being checked out of, the rental car in hand). It also owns the
// per-day stay / timezone lookups and "today in the trip's timezone".
//
// Consumers (this file is the ONE source of these rules):
//   • Worker   — src/lib/mcp-day-view.js, which renders the MCP
//                get_day / get_week tools and the get_trip_info clock
//                for src/lib/mcp.js (TODO #560, #564); src/lib/agenda-core.js,
//                the cross-trip agenda behind MCP get_agenda
//   • Web      — @core/day-plan-core: routes/Days.tsx
//   • Mobile   — mobile/lib/day-plan-core.js, a BYTE-IDENTICAL twin
//                maintained ONLY by `bash scripts/sync_core.sh
//                day-plan-core` (Metro can't import outside mobile/
//                without OTA-fingerprint risk — same arrangement as
//                search-core.js). Never hand-edit the mobile copy.
//                Mobile consumers: lib/day-plan.ts, lib/use-stat-cards.ts,
//                app/day/[date].tsx, app/reservations/index.tsx.
//
// Plan state (cancelled bookings, plan variants) comes from plan-core.js,
// imported by relative path so the same bytes load in all three places:
// the mobile twin imports its own twin, mobile/lib/plan-core.js.
//
// Semantics are pinned by scripts/smoke_day_plan_core.mjs (fixtures from
// live hawaii-sept-26 + japan-winter-2026 bookings) — run it, or the sync
// script, which runs it, after any edit here.
//
// Before this module the rule lived in two places that disagreed: web
// matched `dateStart === D` (a week-long rental showed on pickup day only)
// and mobile matched `dateStart ≤ D ≤ dateEnd` (the same rental showed as
// a 12:00 row on all eight days). Neither showed a return or a check-out.
// There is now one kind-aware span rule and no per-caller coverage
// predicate:
//
//   hotel          check-in row on dateStart (at the booking's `time`,
//                  else the catalog check-in, else 15:00), check-out row
//                  on dateEnd, backdrop (hotelTonight) on the nights
//                  between. Two bookings back to back at the same hotel
//                  are one stay: no rows at the seam.
//   rental         pickup row on dateStart at `time`; return row on a
//                  later dateEnd, also at `time`; backdrop (car) between.
//   flight / rail  depart row on dateStart at `time`. When dateEnd is
//                  after dateStart, an arrive row on dateEnd at
//                  `arrivalTime`; otherwise the single row carries
//                  endTime = arrivalTime.
//   anything else  one row on dateStart.
//
// A cancelled booking (plan-core isActiveBooking) still makes its rows —
// the caller marks them — but it is never the backdrop: not tonight's
// hotel, not the hotel checked out of, not the car.
//
// Plan variants (#567): pass the day (itinerary[date]) as opts.day and
// only the plan the day follows makes rows — slots by plan-core
// isSlotActive, bookings whose `plan` is absent or the day's activePlan.
// Cancelled bookings of that plan keep their rows.
//
// Days that cross zones (#580): pass opts.zoneForRow (rowZoneResolver
// builds one from trip meta, with homeZoneOf's zone as the home) and,
// when the timed rows fall in more than one zone, the day sorts by
// instant instead of by clock time. zoneLabel names a row's zone beside
// its time on such a day ("20:25 JST").
//
// Row keys are prefixed by source ('b:' booking, 's:' slot) so a booking
// and a slot that share an id (japan-winter-2026 has both a booking and a
// slot 'benesse-dinner-jan1') never collide.
//
// Pure; its one import is the sibling plan-core.js, so the Worker, Vite
// and Metro all load the same bytes. Nothing here reads the clock except
// todayInZone / nowInZone, and both take an optional `now`.

import { isActiveBooking, isOpenSlot, isSlotActive } from './plan-core.js';

// ── Kinds, tiers, default times ──────────────────────────────────────

// Booking kinds that read as transit (a leg of travel, not a place you
// dwell at).
export const TRANSIT_KINDS = new Set(['flight', 'rail', 'rental']);

// Kinds whose nights render as the stay backdrop (hotelTonight) rather
// than as timeline rows. A hotel still gets a check-in and a check-out
// row on its end days. Rentals are the `car` backdrop between their end
// days but keep the transit tier, so they are not listed here.
export const BACKDROP_KINDS = new Set(['hotel']);

// Default clock times for rows with no explicit time, so untimed rows sort
// into a sensible place instead of collapsing to the top: transit leaves
// in the morning, dining is dinner, everything else mid-morning.
const TRANSIT_DEFAULT_TIME = '06:00';
const DINING_DEFAULT_TIME = '19:00';
const ACTIVITY_DEFAULT_TIME = '10:00';

// Hotel row defaults when the caller has no catalog check-in / check-out
// time for the property.
export const DEFAULT_CHECK_IN_TIME = '15:00';
export const DEFAULT_CHECK_OUT_TIME = '11:00';

// Same-minute tiebreak, role first: check out of the hotel, then return
// the car, then fly (9/11 at 12:00 is check-out then Avis return); land,
// pick up the car, then check in. Rows without a role (a dinner, a slot)
// come after role rows; tier only breaks ties between rows of equal role.
const TIER_ORDER = { transit: 0, reserved: 1, walkup: 2 };
const ROLE_ORDER = {
  'check-out': 0,
  return: 1,
  depart: 2,
  arrive: 3,
  pickup: 4,
  'check-in': 5,
};
const NO_ROLE_ORDER = 6;

export function isBackdropKind(kind) {
  return !!kind && BACKDROP_KINDS.has(kind);
}

// The tier a booking's rows land in: transit kinds → 'transit', every
// other kind (hotel, restaurant, experience, other) → 'reserved'. Slots
// are always 'walkup'.
export function tierForBookingKind(kind) {
  const k = kind || 'other';
  return TRANSIT_KINDS.has(k) ? 'transit' : 'reserved';
}

// Default time for an untimed booking row of this kind. Hotel rows use
// DEFAULT_CHECK_IN_TIME / DEFAULT_CHECK_OUT_TIME instead.
export function defaultTimeForBookingKind(kind) {
  const k = kind || 'other';
  if (TRANSIT_KINDS.has(k)) return TRANSIT_DEFAULT_TIME;
  if (k === 'restaurant') return DINING_DEFAULT_TIME;
  return ACTIVITY_DEFAULT_TIME;
}

// ── Clock-time parsing ───────────────────────────────────────────────

// First clock time in a free-text string, as 24-hour 'HH:MM'. Handles the
// forms the catalog's hotel checkIn / checkOut fields use ("3:00 PM",
// "12:00 PM (4:00 PM with Amex FHR)", "11:00 AM (confirm …)", "15:00",
// "14:30–18:00", "12:00 noon") and the 'HH:MM' bookings store. A bare
// number with no colon and no am/pm ("up to ~20 hours") is not a time.
// Returns null when nothing parses.
const CLOCK_RE =
  /\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s?m\b\.?|\b(\d{1,2}):(\d{2})\b|\b(noon|midnight)\b/i;

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function parseClockTime(raw) {
  if (typeof raw !== 'string') return null;
  const m = CLOCK_RE.exec(raw);
  if (!m) return null;
  if (m[6]) return m[6].toLowerCase() === 'noon' ? '12:00' : '00:00';
  if (m[1] != null) {
    let h = Number(m[1]);
    const min = m[2] != null ? Number(m[2]) : 0;
    if (min > 59 || h > 23) return null;
    const pm = m[3].toLowerCase() === 'p';
    // "13:00 PM" is a 24-hour time with a stray suffix; leave it alone.
    if (h <= 12) {
      if (h === 12) h = pm ? 12 : 0;
      else if (pm) h += 12;
    }
    return `${pad2(h)}:${pad2(min)}`;
  }
  const h = Number(m[4]);
  const min = Number(m[5]);
  if (h > 23 || min > 59) return null;
  return `${pad2(h)}:${pad2(min)}`;
}

// A stored booking / slot time: normalized when it parses, otherwise the
// raw text as written (so an odd value still shows), '' when empty.
function storedTime(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return '';
  return parseClockTime(s) ?? s;
}

// Resolve a row time from a stored value, falling back to a default.
function resolveTime(raw, fallback) {
  const t = storedTime(raw);
  return t ? { time: t, timeDefaulted: false } : { time: fallback, timeDefaulted: true };
}

// ── Dates ────────────────────────────────────────────────────────────

function addDaysISO(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// dateEnd when it is usable (on or after dateStart), else null.
function endOf(b) {
  return b.dateEnd && b.dateStart && b.dateEnd >= b.dateStart ? b.dateEnd : null;
}

// A booking covers day D when dateStart ≤ D ≤ (dateEnd || dateStart).
// This is plain coverage, NOT the row rule — a hotel covers its middle
// nights but has no row on them. Use classifyDayItems for rows.
export function bookingCoversDate(booking, dateISO) {
  if (!booking || !booking.dateStart || !dateISO) return false;
  const end = endOf(booking) || booking.dateStart;
  return booking.dateStart <= dateISO && dateISO <= end;
}

// ── Hotels and the day backdrop ──────────────────────────────────────

// A booking is a hotel stay when it is filed as kind 'hotel', or when it
// has no specific kind ('other' / missing), links a hotel catalog entry
// (refKind 'hotels', or the singular in older data) AND spans at least
// one night. A restaurant or experience booking that links a hotel entry
// — a dinner at the hotel you're staying in — is not a bed, and neither
// is a one-day fee line filed against the hotel ("Woodlark — destination
// fee + taxes", kind other, no dateEnd), which used to become a second
// check-in.
export function isHotelBooking(b) {
  if (!b) return false;
  if (b.kind === 'hotel') return true;
  const looseKind = !b.kind || b.kind === 'other';
  if (!looseKind || (b.refKind !== 'hotels' && b.refKind !== 'hotel')) return false;
  return !!b.dateStart && !!b.dateEnd && b.dateEnd > b.dateStart;
}

// The morning you leave: dateEnd when it is after dateStart, else the day
// after check-in (a missing or degenerate dateEnd counts as one night).
function checkoutDay(b) {
  if (!b.dateStart) return null;
  return b.dateEnd && b.dateEnd > b.dateStart ? b.dateEnd : addDaysISO(b.dateStart, 1);
}

// Every hotel booked for the NIGHT of D (half-open dateStart ≤ D <
// checkout), in array order, skipping cancelled ones. Usually one; two
// when bookings overlap (a changeover booked a night early, a second
// room). Resolve from bookings, never by joining stays[] to bookings on
// dateStart (that always returned the first hotel of a leg — incident
// 2026-06-08).
export function hotelsForNight(bookings, dateISO) {
  if (!Array.isArray(bookings) || !dateISO) return [];
  return bookings.filter((b) => {
    if (!isHotelBooking(b) || !isActiveBooking(b) || !b.dateStart) return false;
    const end = checkoutDay(b);
    return !!end && b.dateStart <= dateISO && dateISO < end;
  });
}

// The hotel you sleep in the night of D: the first of hotelsForNight.
// Null on a pure checkout day or a day with no hotel booked.
export function hotelForNight(bookings, dateISO) {
  return hotelsForNight(bookings, dateISO)[0] ?? null;
}

// Two bookings at the same hotel, back to back, are one stay: the first
// ends the morning the second starts (the Okura's nights 1–2 on Jake's
// points and night 3 on Liz's, japan-winter-2026). No check-out or
// check-in row falls between them, and the first is not the hotel checked
// out of that morning. Same hotel = the same refId, else the same label
// before its " — " detail. Only active bookings join a stay.
function hotelName(b) {
  const head = String(b.label || '').split(/\s[—–]\s/)[0].trim().toLowerCase();
  return head || null;
}

function sameHotel(a, b) {
  if (a.refId && b.refId) return String(a.refId) === String(b.refId);
  const na = hotelName(a);
  return !!na && na === hotelName(b);
}

function stayContinues(b, bookings, dateISO, side) {
  if (!Array.isArray(bookings) || !isActiveBooking(b)) return false;
  return bookings.some((o) => {
    if (!o || o === b || !isHotelBooking(o) || !isActiveBooking(o) || !sameHotel(b, o)) return false;
    return side === 'next' ? o.dateStart === dateISO : !!endOf(o) && endOf(o) === dateISO;
  });
}

// The hotel you check out of on the morning of D: explicit dateEnd === D,
// unless the stay goes on in a second booking at the same hotel.
export function hotelCheckingOutOn(bookings, dateISO) {
  if (!Array.isArray(bookings) || !dateISO) return null;
  return (
    bookings.find(
      (b) =>
        isHotelBooking(b) &&
        isActiveBooking(b) &&
        !!b.dateEnd &&
        b.dateEnd === dateISO &&
        !stayContinues(b, bookings, dateISO, 'next'),
    ) ?? null
  );
}

// Does any hotel booking cover at least one night of this stay leg?
// Overlap of [stay.dateStart, stay.dateEnd) and [dateStart, checkout).
export function stayHasHotel(bookings, stay) {
  if (!Array.isArray(bookings) || !stay) return false;
  return bookings.some((b) => {
    if (!isHotelBooking(b) || !isActiveBooking(b) || !b.dateStart) return false;
    const end = checkoutDay(b);
    return !!end && b.dateStart < stay.dateEnd && stay.dateStart < end;
  });
}

// What sits behind day D: tonight's hotel, the hotel checked out of this
// morning (can be the same booking only in degenerate data), and the
// rental car you have for at least part of the day (pickup day through
// return day). A row for the check-in / check-out / pickup / return still
// comes from classifyDayItems — callers that show both should skip the
// backdrop card on days where the row already says it.
export function dayBackdrop(bookings, dateISO) {
  const list = Array.isArray(bookings) ? bookings : [];
  return {
    hotelTonight: hotelForNight(list, dateISO),
    hotelCheckingOut: hotelCheckingOutOn(list, dateISO),
    car:
      list.find((b) => b && b.kind === 'rental' && isActiveBooking(b) && bookingCoversDate(b, dateISO)) ?? null,
  };
}

// ── Day rows ─────────────────────────────────────────────────────────

function bookingRow(b, index, role, tier, t, endTime) {
  const id = b.id != null && b.id !== '' ? String(b.id) : `#${index}`;
  return {
    key: role ? `b:${id}:${role}` : `b:${id}`,
    source: 'booking',
    tier,
    role,
    time: t.time,
    timeDefaulted: t.timeDefaulted,
    endTime: endTime || null,
    booking: b,
  };
}

// The rows one booking contributes to day D under the span rule above.
// `all` is the whole booking list, for back-to-back stays at one hotel.
function rowsForBooking(b, index, dateISO, opts, all) {
  if (!b || !b.dateStart || !dateISO) return [];
  const start = b.dateStart;
  const end = endOf(b);
  const kind = b.kind || 'other';
  const rows = [];

  if (isHotelBooking(b)) {
    const onStart = dateISO === start && !stayContinues(b, all, dateISO, 'previous');
    const onEnd = !!end && dateISO === end && !stayContinues(b, all, dateISO, 'next');
    if (!onStart && !onEnd) return rows;
    const times = (opts && typeof opts.hotelTimes === 'function' && opts.hotelTimes(b)) || null;
    if (onStart) {
      // The booking's own time (the check-in the traveler arranged) beats
      // the catalog's standard check-in.
      const t = parseClockTime(b.time) || parseClockTime(times ? times.checkIn : null);
      rows.push(
        bookingRow(b, index, 'check-in', 'reserved',
          t ? { time: t, timeDefaulted: false } : { time: DEFAULT_CHECK_IN_TIME, timeDefaulted: true }),
      );
    }
    if (onEnd) {
      const t = parseClockTime(times ? times.checkOut : null);
      rows.push(
        bookingRow(b, index, 'check-out', 'reserved',
          t ? { time: t, timeDefaulted: false } : { time: DEFAULT_CHECK_OUT_TIME, timeDefaulted: true }),
      );
    }
    return rows;
  }

  if (kind === 'rental') {
    if (dateISO === start) {
      rows.push(bookingRow(b, index, 'pickup', 'transit', resolveTime(b.time, TRANSIT_DEFAULT_TIME)));
    }
    // Bookings carry one `time`, so a same-day rental is its pickup row
    // only; a return row needs a later dateEnd.
    if (end && end > start && dateISO === end) {
      rows.push(bookingRow(b, index, 'return', 'transit', resolveTime(b.time, TRANSIT_DEFAULT_TIME)));
    }
    return rows;
  }

  if (kind === 'flight' || kind === 'rail') {
    const overnight = !!end && end > start;
    if (dateISO === start) {
      rows.push(
        bookingRow(b, index, 'depart', 'transit', resolveTime(b.time, TRANSIT_DEFAULT_TIME),
          overnight ? null : storedTime(b.arrivalTime)),
      );
    }
    if (overnight && dateISO === end) {
      rows.push(bookingRow(b, index, 'arrive', 'transit', resolveTime(b.arrivalTime, TRANSIT_DEFAULT_TIME)));
    }
    return rows;
  }

  if (dateISO === start) {
    rows.push(
      bookingRow(b, index, null, tierForBookingKind(kind), resolveTime(b.time, defaultTimeForBookingKind(kind))),
    );
  }
  return rows;
}

// Sort key for a row time: the 'HH:MM' itself, or '99:99' for an untimed
// slot or a stored time that didn't parse, so those sort after the clock.
const HHMM_RE = /^\d\d:\d\d$/;
function sortTime(t) {
  return HHMM_RE.test(t) ? t : '99:99';
}

// Clock order: time → role → tier. Array.prototype.sort is stable, so
// equal rows keep input order (bookings before slots).
function byClock(a, b) {
  const at = sortTime(a.time);
  const bt = sortTime(b.time);
  if (at !== bt) return at < bt ? -1 : 1;
  const ar = a.role ? ROLE_ORDER[a.role] : NO_ROLE_ORDER;
  const br = b.role ? ROLE_ORDER[b.role] : NO_ROLE_ORDER;
  if (ar !== br) return ar - br;
  return TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
}

// Does this booking belong to the plan the day is following? Same rule as
// slots (plan-core isSlotActive, #586): no `plan` stands in every plan;
// one booked for a variant ("the backup farm tour") only when the day
// follows that variant; plan 'base' only while the day follows the base
// plan; a plan id the day doesn't declare reads as shared, so the booking
// stays on screen. Status is not looked at: a cancelled booking of the
// active plan keeps its rows.
function inDayPlan(b, day) {
  return !!b && isSlotActive(b, day);
}

// Every row on day D: booking rows under the span rule plus the day's
// itinerary slots, sorted. Sort order:
//   1. time ('HH:MM'; untimed or unparseable sorts last)
//   2. role: check-out < return < depart < arrive < pickup < check-in <
//      no role
//   3. tier: transit < reserved < walkup
//   4. input order (bookings before slots; Array.prototype.sort is stable)
// Options:
//   hotelTimes(booking) → { checkIn, checkOut } free-text times from the
//     hotel's catalog entry ("3:00 PM"). Check-in uses the booking's own
//     `time` first, then checkIn; check-out uses checkOut. Missing /
//     unparseable → 15:00 / 11:00 with timeDefaulted: true.
//   day — itinerary[D] ({ slots, plans?, activePlan? }). When given, only
//     the plan the day follows makes rows: slots where plan-core
//     isSlotActive, bookings with no `plan` or the day's activePlan.
//     Without it every slot and booking passed in makes rows (as before).
//   zoneForRow(row, rows) → IANA zone | null — the zone a row's time is
//     local to. Every row then carries `zone`. When the timed rows span
//     more than one zone (rowsSpanZones), the sort's first key is the
//     instant (the row's date + time in its zone); the clock order above
//     breaks ties and untimed rows still go last. One zone, or any timed
//     row without a usable zone, keeps the clock order. rowZoneResolver
//     (below) builds one from trip meta.
// Cancelled bookings keep their rows; the caller marks them. Slots of type
// 'open' (plan-core isOpenSlot) make no rows.
export function classifyDayItems(bookings, slots, dateISO, opts) {
  const day = opts && opts.day !== undefined ? opts.day : undefined;
  const items = [];
  const bList = Array.isArray(bookings) ? bookings : [];
  for (let i = 0; i < bList.length; i++) {
    if (day !== undefined && !inDayPlan(bList[i], day)) continue;
    for (const row of rowsForBooking(bList[i], i, dateISO, opts, bList)) items.push(row);
  }
  const sList = Array.isArray(slots) ? slots : [];
  for (let i = 0; i < sList.length; i++) {
    const s = sList[i];
    if (!s) continue;
    // A part of the day marked open is a gap on purpose, not a row (#597).
    if (isOpenSlot(s)) continue;
    if (day !== undefined && !isSlotActive(s, day)) continue;
    items.push({
      key: s.slotId != null && s.slotId !== '' ? `s:${s.slotId}` : `s:#${i}`,
      source: 'slot',
      tier: 'walkup',
      role: null,
      time: storedTime(s.time),
      timeDefaulted: false,
      endTime: null,
      slot: s,
    });
  }
  const zoneFor = opts && typeof opts.zoneForRow === 'function' ? opts.zoneForRow : null;
  if (!zoneFor) {
    items.sort(byClock);
    return items;
  }
  const zones = items.map((it) => {
    const z = zoneFor(it, items);
    return zoneFormatter(z) ? z : null;
  });
  items.forEach((it, i) => {
    it.zone = zones[i];
  });
  if (!rowsSpanZones(items)) {
    items.sort(byClock);
    return items;
  }
  const instant = new Map(
    items.map((it) => [it, HHMM_RE.test(it.time) ? zonedInstant(dateISO, it.time, it.zone) : null]),
  );
  items.sort((a, b) => {
    const ai = instant.get(a);
    const bi = instant.get(b);
    if (ai != null && bi != null && ai !== bi) return ai - bi;
    if ((ai == null) !== (bi == null)) return ai == null ? 1 : -1;
    return byClock(a, b);
  });
  return items;
}

// True when the timed rows (those with an 'HH:MM' time) carry more than
// one zone and every one of them has a zone — the case where
// classifyDayItems sorted by instant. False for rows built without
// zoneForRow.
export function rowsSpanZones(rows) {
  const seen = new Set();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || !HHMM_RE.test(r.time)) continue;
    if (!r.zone) return false;
    seen.add(r.zone);
  }
  return seen.size > 1;
}

// ── Zones for rows (#580) ────────────────────────────────────────────

// One cached formatter per zone; null for a zone Intl doesn't know.
const zoneFormatters = new Map();
function zoneFormatter(tz) {
  if (typeof tz !== 'string' || !tz) return null;
  if (zoneFormatters.has(tz)) return zoneFormatters.get(tz);
  let fmt = null;
  try {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    fmt = null;
  }
  zoneFormatters.set(tz, fmt);
  return fmt;
}

// How far `tz` is ahead of UTC at instant `t` (ms).
function zoneOffsetMs(fmt, t) {
  const parts = fmt.formatToParts(new Date(t));
  const get = (type) => Number((parts.find((x) => x.type === type) || {}).value);
  const h = get('hour') === 24 ? 0 : get('hour');
  return Date.UTC(get('year'), get('month') - 1, get('day'), h, get('minute'), get('second')) - t;
}

// The instant (ms since epoch) of wall time `time` ('HH:MM') on
// `dateISO` in zone `tz`; null when any input is unusable. A wall time a
// DST change skips or repeats resolves to an instant within the hour.
export function zonedInstant(dateISO, time, tz) {
  const fmt = zoneFormatter(tz);
  if (!fmt || typeof dateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return null;
  if (typeof time !== 'string' || !HHMM_RE.test(time)) return null;
  const [y, mo, d] = dateISO.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  const first = wall - zoneOffsetMs(fmt, wall);
  return wall - zoneOffsetMs(fmt, first);
}

// Short labels for the zones the trips use, where Intl's en-US short name
// is an offset ("GMT+9") or the engine has none; mobile's labels before
// this moved into the core (lib/trip-zones.ts).
const ZONE_ABBR = {
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Taipei': 'TST',
  'Pacific/Honolulu': 'HST',
  'America/Los_Angeles': 'PT',
  'America/Vancouver': 'PT',
  'America/Denver': 'MT',
  'America/Chicago': 'CT',
  'America/New_York': 'ET',
  'Europe/London': 'UK',
  'Europe/Dublin': 'IE',
};
const zoneNameFormatters = new Map();
function zoneShortName(tz, dateISO) {
  let fmt = zoneNameFormatters.get(tz);
  if (fmt === undefined) {
    try {
      fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
    } catch {
      fmt = null;
    }
    zoneNameFormatters.set(tz, fmt);
  }
  if (!fmt) return null;
  try {
    const part = fmt.formatToParts(new Date(`${dateISO}T12:00:00Z`)).find((p) => p.type === 'timeZoneName');
    return part ? part.value : null;
  } catch {
    return null;
  }
}

// Short label for a row's zone on a day that crosses zones ("20:25 JST",
// "08:15 HST"). With `dateISO`, Intl's en-US short name on that day when
// it is a letter abbreviation (HST, PST / PDT, AKST, GMT); else the table
// above; else the IANA city ("Asia/Bangkok" → "Bangkok"). Without a date
// the table, else the city: no clock read, so a daylight-saving zone reads
// "PT" rather than guessing PST or PDT. '' for no zone.
export function zoneLabel(tz, dateISO) {
  if (typeof tz !== 'string' || !tz) return '';
  if (typeof dateISO === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateISO)) {
    const name = zoneShortName(tz, dateISO);
    if (name && /^[A-Z]{2,5}$/.test(name)) return name;
  }
  if (ZONE_ABBR[tz]) return ZONE_ABBR[tz];
  return (tz.split('/').pop() || tz).replace(/_/g, ' ');
}

// The IANA zone a free-text time names, the way the reservation tracker
// writes opensAt ("00:00 JST", "10:00 KST", "9am PT"): the first whole
// upper-case word that is one of the labels above (or a US standard /
// daylight variant). Null when the text names none. "UK" and "IE" are left
// out: as words they mean too many other things.
const LABEL_ZONE = {
  JST: 'Asia/Tokyo',
  KST: 'Asia/Seoul',
  HST: 'Pacific/Honolulu',
  PT: 'America/Los_Angeles',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  MT: 'America/Denver',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  CT: 'America/Chicago',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  ET: 'America/New_York',
  EST: 'America/New_York',
  EDT: 'America/New_York',
};
export function zoneFromText(text) {
  if (typeof text !== 'string') return null;
  for (const word of text.match(/\b[A-Z]{2,4}\b/g) || []) {
    if (LABEL_ZONE[word]) return LABEL_ZONE[word];
  }
  return null;
}

// Origin and destination of a flight / rail booking from `airports`
// (canonical) or the legacy `route`: "NGO → HNL", "PDX → SEA → NRT",
// "PDX-SEA-NRT". A string with an arrow splits on arrows only, so
// "Kyoto → Shin-Osaka" keeps its hyphen. Upper-cased; null when there
// aren't two ends.
function legEnds(b) {
  const raw = typeof b.airports === 'string' && b.airports.trim() ? b.airports : b.route;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const sep = /→|->|>/.test(raw) ? /\s*(?:→|->|>)\s*/ : /\s*(?:–|—|-|\/)\s*/;
  const parts = raw
    .split(sep)
    .map((p) => p.trim().toUpperCase())
    .filter(Boolean);
  return parts.length >= 2 ? { from: parts[0], to: parts[parts.length - 1] } : null;
}

// Does depart row `row` leave from where the traveler has already
// crossed into the arriving zone? True when another flight / rail row on
// the day lands where this one leaves from, and that row is either an
// arrive row (an overnight leg landing today) or a one-row leg that
// lands at or before the clock time it left — the mark of a leg that
// crossed zones eastward (JL798 NGO 20:25 → HNL 08:15, so the AS1066
// HNL → OGG hop after it leaves on Honolulu time). A connection between
// two same-zone hops (PDX → SEA, SEA → HNL booked as two legs) stays in
// the leaving zone.
function leavesAfterCrossing(row, rows) {
  const ends = legEnds(row.booking);
  if (!ends) return false;
  return rows.some((r) => {
    if (r === row || r.source !== 'booking' || r.booking === row.booking) return false;
    if (r.booking.kind !== 'flight' && r.booking.kind !== 'rail') return false;
    const other = legEnds(r.booking);
    if (!other || other.to !== ends.from) return false;
    if (r.role === 'arrive') return true;
    return (
      r.role === 'depart' && !!r.endTime && HHMM_RE.test(r.time) && HHMM_RE.test(r.endTime) && r.endTime <= r.time
    );
  });
}

// Where the travelers live, as { timezone, tzSource }: meta.homeTimezone
// ('home'), else the older trip-state field tripBrief.homeTimezone that
// the in-app assistant reads ('home:tripBrief'). Null when neither is a
// zone Intl knows. Every surface builds rowZoneResolver's home zone with
// this (MCP get_day via mcp-day-view.js, web Days).
export function homeZoneOf(meta, state) {
  if (meta && zoneFormatter(meta.homeTimezone)) return { timezone: meta.homeTimezone, tzSource: 'home' };
  const brief = state && state.tripBrief ? state.tripBrief.homeTimezone : null;
  if (zoneFormatter(brief)) return { timezone: brief, tzSource: 'home:tripBrief' };
  return null;
}

// Place a row with no zone of its own (a slot, a restaurant or experience
// booking) against the day's one crossing leg by whether its time is
// possible on each side:
//   • read in the arriving zone it falls before the leg lands → the zone
//     being left (the 08:00 "Uber to PDX" before the 10:30 PDX departure);
//   • read in the leaving zone it falls after the leg departs → the
//     arriving zone (the 17:30 dinner on Oʻahu).
// When both readings are possible (only on a leg that lands at an earlier
// clock time than it left, JL798 NGO 20:25 → HNL 08:15, where any time
// between 08:15 and 20:25 fits either side), the side with more room
// between the row and the flight wins: Kyoto lunch at 12:00 (8h25 before
// the departure, 3h45 after the landing) stays in Kyoto, an 18:00 lūʻau
// goes to Maui. Null when neither reading is possible (a time inside the
// flight) or both leave the same room, and the caller uses the day's zone.
function zoneByFeasibility(row, dateISO, leg) {
  const inArriving = zonedInstant(dateISO, row.time, leg.to);
  const inLeaving = zonedInstant(dateISO, row.time, leg.from);
  if (inArriving == null || inLeaving == null) return null;
  const arrivingOk = inArriving >= leg.land;
  const leavingOk = inLeaving <= leg.dep;
  if (arrivingOk && !leavingOk) return leg.to;
  if (leavingOk && !arrivingOk) return leg.from;
  if (!arrivingOk) return null;
  const roomLeaving = leg.dep - inLeaving;
  const roomArriving = inArriving - leg.land;
  if (roomLeaving === roomArriving) return null;
  return roomLeaving > roomArriving ? leg.from : leg.to;
}

// A zoneForRow for day D of a trip, built from its meta: stays[] with
// their `timezone`, meta.timezone, and meta.homeTimezone (opts.homeTimezone
// wins when given; homeZoneOf builds it the way every surface does). Row
// rules:
//   depart           the zone being left: the stay ending on D, else the
//                    stay covering the night before, else home on or
//                    before the first stay's first day. A leg that leaves
//                    from where an earlier leg crossed into the arriving
//                    zone (leavesAfterCrossing) takes the arriving zone.
//   arrive           the arriving zone: the stay starting on D, home from
//                    the last stay's last day on (a same-day flight home
//                    lands at home), else the day's zone.
//   hotel check-out  the zone of the leg the hotel's last night is on.
//   hotel check-in,  the day's zone: timezoneForDate (the arriving leg
//   rental pickup /  wins a transition day), or home before the first
//   return           stay or after the last.
//   everything else  (slots, restaurant / experience / other bookings)
//                    on a day with exactly one crossing leg — a flight or
//                    train row whose leaving and arriving zones differ and
//                    that lands on D — the side of the leg its time is
//                    possible on (zoneByFeasibility); otherwise, and for an
//                    untimed row or a default time, the day's zone.
// A stay without its own timezone uses meta.timezone. A row it can't
// place gets null, and classifyDayItems keeps clock order.
export function rowZoneResolver(meta, dateISO, opts) {
  const home =
    (opts && zoneFormatter(opts.homeTimezone) ? opts.homeTimezone : null) ||
    (meta && zoneFormatter(meta.homeTimezone) ? meta.homeTimezone : null);
  const metaTz = meta && zoneFormatter(meta.timezone) ? meta.timezone : null;
  const stays = (meta && Array.isArray(meta.stays) ? meta.stays : []).filter(
    (s) => s && typeof s.dateStart === 'string' && typeof s.dateEnd === 'string' && s.dateStart <= s.dateEnd,
  );
  let firstStart = null;
  let lastEnd = null;
  for (const s of stays) {
    if (!firstStart || s.dateStart < firstStart) firstStart = s.dateStart;
    if (!lastEnd || s.dateEnd > lastEnd) lastEnd = s.dateEnd;
  }
  const zoneOf = (s) => (s && zoneFormatter(s.timezone) ? s.timezone : metaTz);
  const outside = (d) => stays.length > 0 && (d < firstStart || d > lastEnd);
  const dayZone = () => (home && outside(dateISO) ? home : timezoneForDate(meta, dateISO));
  const prev = addDaysISO(dateISO, -1);

  const leavingZone = () => {
    const ending = stays.find((s) => s.dateEnd === dateISO && s.dateStart < dateISO);
    if (ending) return zoneOf(ending);
    const lastNight = stays.find((s) => s.dateStart <= prev && prev < s.dateEnd);
    if (lastNight) return zoneOf(lastNight);
    if (home && (!stays.length || dateISO <= firstStart)) return home;
    return dayZone();
  };
  // Home from the last stay's last day on: a leg that leaves that day
  // (AS 814 HNL 12:15 → PDX 20:50 on 9/11) lands at home.
  const arrivingZone = () => {
    const starting = stays.find((s) => s.dateStart === dateISO);
    if (starting) return zoneOf(starting);
    if (home && stays.length && dateISO >= lastEnd) return home;
    return dayZone();
  };

  const departZone = (row, rows) => (leavesAfterCrossing(row, rows) ? arrivingZone() : leavingZone());

  // The day's crossing leg { from, to, dep, land } (instants in ms), or
  // null unless exactly one flight / rail row crosses zones and lands on
  // D: a one-row leg with an arrival time, or the arrive row of an
  // overnight leg. Two crossings in a day are left to the day's zone.
  const crossingOf = (rows) => {
    const legs = [];
    for (const r of rows) {
      if (!r || r.source !== 'booking' || !r.booking) continue;
      const b = r.booking;
      if ((b.kind !== 'flight' && b.kind !== 'rail') || r.timeDefaulted) continue;
      let from = null;
      let to = null;
      let dep = null;
      let land = null;
      if (r.role === 'depart' && r.endTime) {
        from = departZone(r, rows);
        to = arrivingZone();
        dep = zonedInstant(dateISO, r.time, from);
        land = zonedInstant(dateISO, r.endTime, to);
      } else if (r.role === 'arrive') {
        from = leavingZone();
        to = arrivingZone();
        dep = zonedInstant(b.dateStart, parseClockTime(b.time), from);
        land = zonedInstant(dateISO, r.time, to);
      } else {
        continue;
      }
      if (from && to && from !== to && dep != null && land != null) legs.push({ from, to, dep, land });
    }
    return legs.length === 1 ? legs[0] : null;
  };
  let crossingRows = null;
  let crossing = null;

  return (row, rows) => {
    if (!row) return null;
    const list = Array.isArray(rows) ? rows : [];
    if (!row.role) {
      // A slot, or a booking with no role (restaurant, experience, other).
      if (!HHMM_RE.test(row.time) || row.timeDefaulted) return dayZone();
      if (list !== crossingRows) {
        crossingRows = list;
        crossing = crossingOf(list);
      }
      return (crossing && zoneByFeasibility(row, dateISO, crossing)) || dayZone();
    }
    if (row.source !== 'booking') return dayZone();
    switch (row.role) {
      case 'depart':
        return departZone(row, list);
      case 'arrive':
        return arrivingZone();
      case 'check-out': {
        const leg = stayForDate(stays, prev);
        return leg ? zoneOf(leg) : leavingZone();
      }
      default:
        return dayZone();
    }
  };
}

// ── Stays, timezones, today ──────────────────────────────────────────

// The stay leg day D belongs to. The leg you sleep in wins a transition
// day (dateStart ≤ D < dateEnd); the departure day (D === dateEnd) falls
// back to the leg that just ended so the last trip day isn't orphaned.
export function stayForDate(stays, dateISO) {
  if (!Array.isArray(stays) || !dateISO) return null;
  let fallback = null;
  for (const s of stays) {
    if (!s || !s.dateStart || !s.dateEnd) continue;
    if (dateISO >= s.dateStart && dateISO < s.dateEnd) return s;
    if (dateISO === s.dateEnd) fallback = s;
  }
  return fallback;
}

// The IANA timezone for day D: the stay covering D that carries a
// `timezone` (same leg rule as stayForDate), else meta.timezone, else null.
export function timezoneForDate(meta, dateISO) {
  if (!meta) return null;
  const zoned = Array.isArray(meta.stays)
    ? meta.stays.filter((s) => s && typeof s.timezone === 'string' && s.timezone)
    : [];
  const stay = stayForDate(zoned, dateISO);
  if (stay) return stay.timezone;
  return typeof meta.timezone === 'string' && meta.timezone ? meta.timezone : null;
}

function localNow(d) {
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}

// Wall-clock date and time in `tz` at instant `now` (default: the current
// time). A null, empty or unknown timezone falls back to the device's
// local calendar.
export function nowInZone(tz, now) {
  const d = now == null ? new Date() : new Date(now instanceof Date ? now.getTime() : now);
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(d);
      const get = (type) => {
        const p = parts.find((x) => x.type === type);
        return p ? p.value : '';
      };
      const y = get('year');
      const mo = get('month');
      const day = get('day');
      let h = get('hour');
      const mi = get('minute');
      if (y && mo && day && h && mi) {
        if (h === '24') h = '00'; // engines that ignore hourCycle
        return { date: `${y}-${mo}-${day}`, time: `${h.padStart(2, '0')}:${mi}` };
      }
    } catch {
      // Unknown timezone or no Intl timezone support — use device local.
    }
  }
  return localNow(d);
}

// Today's date ('YYYY-MM-DD') in `tz`. Same fallback as nowInZone.
export function todayInZone(tz, now) {
  return nowInZone(tz, now).date;
}
